import React from 'react';
import { connect } from 'formik';
import { withForwardRef } from 'hoc';
import { get, isFunction, noop } from 'lodash';
import { OnBlur } from 'utils/types/react';

interface FormProps {
  name: string;
  formik?: any;
  errorMessage?: Function | string;
  touched: boolean;
  invalid: boolean;
  wasSubmitted: boolean;
  onChange?: Function;
  onBlur?: OnBlur;
  forwardedRef?: React.Ref<HTMLFormElement>;
}

const defaultProps = {
  onChange: noop as any,
  onBlur: noop as any,
  errorMessage: '' as any,
};

function withForm<ComponentProps>(
  Component: React.ComponentType<ComponentProps>,
) {
  function FormElement(props: ComponentProps & FormProps) {
    function getErrorMessage() {
      const { errorMessage, formik, name } = props;
      const error = get(formik.errors, name);
      const touched = get(formik.touched, name);

      if (errorMessage) {
        return isFunction(errorMessage)
          ? (errorMessage as Function)({ touched, error, name })
          : errorMessage;
      }

      return error || '';
    }

    function onChangeHandler(...[data, ...rest]) {
      const { onChange, name, formik } = props;

      const value = data?.target?.value ?? data;

      formik.setFieldValue(name, value);
      onChange && onChange(value, ...rest);
    }

    function onBlurHandler(event) {
      const { onBlur = noop, name, formik } = props;
      formik.handleBlur(name)(event);
      onBlur(event);
    }

    const { name, formik } = props;

    const componentProperties = {
      ...props,
      //ref: forwardedRef,
      name,
      // TextInput, Switch, CheckBoxGroup, Select/Picker...
      onChange: onChangeHandler,
      onBlur: onBlurHandler,
      value: get(formik.values, name),
      touched: get(formik.touched, name),
      invalid: Boolean(getErrorMessage()),
      errorMessage: getErrorMessage(),
      wasSubmitted: formik.submitCount > 0,
    };

    return <Component {...componentProperties} withForm />;
  }

  FormElement.defaultProps = defaultProps;

  return FormElement;
}

export default function <ComponentProps>(
  Component: React.ComponentType<ComponentProps>,
) {
  return withForwardRef<HTMLFormElement, ComponentProps & FormProps>(
    connect(withForm<ComponentProps>(Component)),
  );
}
